import { useState, useEffect } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Cookie, X } from 'lucide-react';
import { Link } from 'react-router-dom';
import { useLanguage } from '@/context/LanguageContext';
import { getLocalizedPath } from '@/config/routes';

const COOKIE_CONSENT_KEY = 'netblitz-cookie-consent';

// Cookie banner translations
const cookieTexts = {
  nl: {
    title: 'Wij gebruiken cookies',
    description: 'We gebruiken cookies om je ervaring te verbeteren en onze website te analyseren. Door op "Alles accepteren" te klikken, ga je akkoord met ons',
    privacy: 'privacybeleid',
    necessary: 'Alleen noodzakelijk',
    acceptAll: 'Alles accepteren'
  },
  en: {
    title: 'We use cookies',
    description: 'We use cookies to improve your experience and analyze our website. By clicking "Accept all", you agree to our',
    privacy: 'privacy policy',
    necessary: 'Necessary only',
    acceptAll: 'Accept all'
  },
  fr: {
    title: 'Nous utilisons des cookies',
    description: 'Nous utilisons des cookies pour améliorer votre expérience et analyser notre site. En cliquant sur "Tout accepter", vous acceptez notre',
    privacy: 'politique de confidentialité',
    necessary: 'Nécessaires uniquement',
    acceptAll: 'Tout accepter'
  },
  de: {
    title: 'Wir verwenden Cookies',
    description: 'Wir verwenden Cookies, um Ihre Erfahrung zu verbessern und unsere Website zu analysieren. Durch Klicken auf "Alle akzeptieren" stimmen Sie unserer',
    privacy: 'Datenschutzrichtlinie',
    necessary: 'Nur notwendige',
    acceptAll: 'Alle akzeptieren'
  }
};

export const CookieConsent = () => {
  const { language } = useLanguage();
  const [isVisible, setIsVisible] = useState(false);
  
  const texts = cookieTexts[language] || cookieTexts.nl;

  useEffect(() => {
    const consent = localStorage.getItem(COOKIE_CONSENT_KEY);
    if (!consent) {
      // Show after a small delay
      const timer = setTimeout(() => setIsVisible(true), 2000);
      return () => clearTimeout(timer);
    }
  }, []);

  const acceptAll = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics: true,
      marketing: true,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  const acceptNecessary = () => {
    localStorage.setItem(COOKIE_CONSENT_KEY, JSON.stringify({
      necessary: true,
      analytics: false,
      marketing: false,
      timestamp: new Date().toISOString()
    }));
    setIsVisible(false);
  };

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ y: 100, opacity: 0 }}
          animate={{ y: 0, opacity: 1 }}
          exit={{ y: 100, opacity: 0 }}
          transition={{ type: 'spring', damping: 25 }}
          className="fixed bottom-0 left-0 right-0 z-[1002] p-4 md:p-6"
          data-testid="cookie-consent"
        >
          <div className="max-w-4xl mx-auto bg-white rounded-2xl shadow-2xl border border-gray-200 overflow-hidden">
            <div className="p-4 md:p-6 md:flex md:items-center md:gap-6">
              {/* Icon */}
              <div className="hidden md:flex items-center justify-center w-14 h-14 bg-blue-100 rounded-xl flex-shrink-0">
                <Cookie className="w-7 h-7 text-[#2563EB]" />
              </div>
              
              {/* Content */}
              <div className="flex-grow">
                <h3 className="text-base md:text-lg font-semibold text-[#111827] mb-1 md:mb-2 flex items-center gap-2">
                  <Cookie className="w-5 h-5 text-[#2563EB] md:hidden" />
                  {texts.title}
                </h3>
                <p className="text-xs md:text-sm text-[#4B5563] leading-relaxed">
                  {texts.description}{' '}
                  <Link to={getLocalizedPath('privacy', language)} className="text-[#2563EB] hover:underline">{texts.privacy}</Link>.
                </p>
              </div>
              
              {/* Buttons - Touch friendly min 44px height */}
              <div className="flex flex-row gap-2 sm:gap-3 mt-3 md:mt-0 md:flex-shrink-0">
                <button
                  onClick={acceptNecessary}
                  className="flex-1 md:flex-none px-3 sm:px-5 py-3 md:py-3.5 text-sm md:text-base font-medium text-[#4B5563] bg-[#F3F4F6] hover:bg-gray-200 rounded-xl transition-colors min-h-[44px] whitespace-nowrap"
                  data-testid="cookie-necessary-btn"
                >
                  {texts.necessary}
                </button>
                <button
                  onClick={acceptAll}
                  className="flex-1 md:flex-none px-3 sm:px-5 py-3 md:py-3.5 text-sm md:text-base font-medium text-white bg-[#2563EB] hover:bg-[#1D4ED8] rounded-xl transition-colors min-h-[44px] whitespace-nowrap"
                  data-testid="cookie-accept-btn"
                >
                  {texts.acceptAll}
                </button>
              </div>
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};
